import React, { useState, useCallback, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import qs from 'qs';
import debounce from 'lodash/debounce';
import startCase from 'lodash/startCase';

import { FontAwesomeIcon as FA } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus';
import { faTrash } from '@fortawesome/free-solid-svg-icons/faTrash';
import { faUpload } from '@fortawesome/free-solid-svg-icons/faUpload';
import { faDownload } from '@fortawesome/free-solid-svg-icons/faDownload';

import { resolveTo } from '~/commons/helpers';
import useHttpStatus from '~/commons/hooks/useHttpStatus';
import useComparable from '~/commons/hooks/useComparable';

import ListHttpTable from '~/commons/components/ListHttpTable.jsx';
import FormHttpBtn from '~/commons/components/FormHttpBtn.jsx';
import FormHttpFileBtn from '~/commons/components/FormHttpFileBtn.jsx';
import ModalConfirm from '~/commons/components/ModalConfirm.jsx';

const PageResource = ({
  api,
  sort,
  cols,
  searchable = false,
  filter = {},
  actions: {
    fileImport = false,
    fileExport = false,
    find = true,
    create = true,
    remove = true,
    jsonExport = false,
  } = {},
  basePath = '',
  onRowClick,
  onRefresh = () => {},
  ctrls = [],
  children = () => {},
}) => {
  const history = useHistory();
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState('');
  const [fSearch, setFSearch] = useState('');
  const [searchBy, setSearchBy] = useState('');
  const [refreshCount, setRefreshCount] = useState(0);
  const [res, req] = useHttpStatus();
  const [removeConfirm, setRemoveConfirm] = useState(false);
  const debouncedSearch = useCallback(debounce(
    (v) => setFSearch(v),
    1500,
    { leading: true },
  ), []);
  let searchOpts;

  if (Array.isArray(searchable)) {
    const c = searchable.find((s) => s.value === searchBy);

    searchOpts = c?.options;
  }

  const bPath = typeof basePath === 'function'
    ? basePath
    : () => basePath;
  const f = { ...filter };

  if (searchable && fSearch) {
    f.search = fSearch;
    if (searchBy) f.searchBy = searchBy;
  }

  const refresh = () => {
    setRefreshCount(refreshCount + 1);
    onRefresh();
  };

  useEffect(() => {
    setSearchBy(
      Array.isArray(searchable)
        ? searchable[0].value || searchable[0]
        : '',
    );
  }, [useComparable(searchable)]);

  return (
    <div>
      {remove && (
        <ModalConfirm
          show={removeConfirm}
          onReject={() => setRemoveConfirm(false)}
          onConfirm={async () => {
            setRemoveConfirm(false);

            await req({
              url: api,
              method: 'delete',
              data: { _id: selected.map((s) => s._id) },
            });

            refresh();
          }}
        >
          Are you sure you want to delete?
        </ModalConfirm>
      )}
      <div className="pb-3 clearfix">
        {create && (
          <Link
            to={resolveTo(bPath('new'), 'new')}
            className="btn btn-primary mr-2"
          >
            <FA icon={faPlus} fixedWidth />
          </Link>
        )}
        {fileImport && (
          <FormHttpFileBtn
            className="btn btn-secondary mr-2"
            icon={faUpload}
            onSuccess={() => refresh()}
            api={{
              url: `${api}/${
                typeof fileImport === 'string' ? fileImport : 'import'
              }`,
              ...fileImport,
            }}
            fileOpts={{
              accept: '.csv',
            }}
          />
        )}
        {fileExport && (
          <div className="btn-group mr-2">
            <a
              className="btn btn-secondary"
              href={`${api}/${typeof fileExport === 'string' ? fileExport : 'export'}${Object.values(f).filter((s) => s).length ? `?${qs.stringify({ filter: f })}` : ''}`}
            >
              <FA icon={faDownload} fixedWidth /> CSV
            </a>

          </div>
        )}
        {jsonExport && (
             <a
              className="btn btn-primary ml-2"
              href={`${api}/${typeof fileExport === 'string' ? fileExport : 'export'}.json${Object.values(f).filter((s) => s).length ? `?${qs.stringify({ filter: f })}` : ''}`}
            >
              <FA icon={faDownload} fixedWidth /> JSON
            </a>
        )}
        {remove && selected.length > 0 && (
          <FormHttpBtn
            res={res}
            className="btn btn-link"
            icon={faTrash}
            onClick={() => setRemoveConfirm(true)}
          />
        )}
        {children({
          selected,
          refresh,
        })}
      </div>
      <ListHttpTable
        {...{
          api: { url: api },
          sort,
          cols,
          filter: f,
          refreshCount,
          onSelect: remove
            ? (s) => { setSelected(s); }
            : undefined,
          ...(find ? {
            onRowClick: onRowClick || ((row) => {
              history.push(resolveTo(bPath(row), row._id));
            }),
          } : {}),
        }}
      >
        <div className="form-row">
          {searchable && (
            <div className="col">
              <div className="input-group">
                {Array.isArray(searchable) && (
                  <div className="input-group-prepend">
                    <select
                      name="searchBy"
                      className="custom-select"
                      value={searchBy}
                      onChange={(e) => {
                        setSearch('');
                        debouncedSearch('');
                        setSearchBy(e.target.value);
                      }}
                    >
                      {searchable.map((by) => (
                        <option
                          key={by.value || by}
                          value={by.value || by}
                        >
                          {by.label || startCase(by)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {searchOpts ? (
                  <select
                    className="custom-select"
                    value={search}
                    onChange={(e) => {
                      const { value } = e.target;

                      setSearch(value);
                      debouncedSearch(value);
                    }}
                  >
                    <option value="">Show All</option>
                    {searchOpts.map((o) => (
                      <option
                        key={o.value}
                        value={o.value}
                      >{o.label}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search..."
                    value={search}
                    onChange={(e) => {
                      const { value } = e.target;

                      setSearch(value);
                      debouncedSearch(value);
                    }}
                  />
                )}
              </div>
            </div>
          )}
          {ctrls.map((c, i) => (
            <div className="col-auto" key={i}>
              {c}
            </div>
          ))}
        </div>
      </ListHttpTable>
    </div>
  );
};

export default PageResource;
